/** Who has answered — off by default, `G` or one tap away (UX §4, §6.2).
 *
 * This is the densest thing on the screen, the least glanceable, and the only surface here
 * that prints a student's name. It used to be always on, which is a real risk for a teacher
 * who mirrors their controller onto the projector by accident. It is unchanged in content and
 * simply no longer on the default screen; peers never see it in any case.
 */
import { useTranslation } from "react-i18next";

import { Button } from "../ui";
import { answeredByChannel } from "./channels";
import type { Snapshot } from "./useLive";

type Props = {
  snapshot: Snapshot;
  /** The session has ended, so `label_answers` would be refused: the verdicts are frozen. */
  ended: boolean;
  /** The session runs `join_mode = anonymous`: the channel is a count, never a row's mark. */
  anonymous?: boolean;
  onHide: () => void;
  onLabel: (clusterId: string, correct: boolean | null) => void;
};

export function RoomPanel({ snapshot, ended, anonymous, onHide, onLabel }: Props) {
  const { t } = useTranslation();
  const agg = snapshot.aggregate;
  const respondents = snapshot.respondents ?? [];
  const clusters = agg?.clusters ?? [];
  const shortText = snapshot.item?.snapshot.type === "short_text";
  // The channel marker (FR-051) reads `per_respondent[rid].channel`, which is staff-only
  // already. In anonymous join mode a per-row attribute could single a student out, so the
  // channel is stated as a count of this round's answers and marked on no row.
  const byChannel = anonymous ? answeredByChannel(agg) : [];

  return (
    <section className="flex flex-col gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-label={t("live.grid")}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <h2 className="font-semibold">{t("live.grid")}</h2>
        <Button variant="tertiary" onClick={onHide}>{t("live.hideGrid")}</Button>
      </div>
      {respondents.length === 0 && <p className="text-sm text-[color:var(--ink-2)]">{t("live.lobbyHint", { count: 0 })}</p>}
      {byChannel.length > 0 && (
        <p className="text-sm text-[color:var(--ink-2)]" data-room-channel-count>
          {byChannel
            .map((c) =>
              // a card's and a clicker's counts have their own wording: "in card" reads wrongly
              c.channel === "card"
                ? t("live.channel.printed.count", { count: c.count })
                : c.channel === "clicker"
                  ? t("live.channel.clicker.count", { count: c.count })
                  : t("live.channel.count", { count: c.count, channel: t(`live.channel.names.${c.channel}`, { defaultValue: c.channel }) }),
            )
            .join(" · ")}
        </p>
      )}
      <ul className="flex flex-wrap gap-1" aria-label={t("live.grid")}>
        {respondents.map((rid, i) => {
          const r = agg?.per_respondent?.[rid];
          const tone = !r ? "var(--ink-2)" : r.correct === true ? "var(--secure)" : r.correct === false ? "var(--not-yet)" : "var(--accent)";
          const label = snapshot.labels?.[rid] || `#${i + 1}`;
          return (
            <li
              key={rid}
              className="flex min-h-8 items-center gap-1 rounded-full border px-2 text-sm"
              // a clicker's "I'm lost" chord (STORY-086) is no answer: an outline and its own
              // glyph and words, never the colour of an answer sent
              style={{ background: r && !r.lost ? tone : "transparent", borderColor: tone, borderStyle: r?.lost ? "dashed" : undefined, color: r && !r.lost ? "var(--accent-ink)" : "inherit" }}
              data-room-lost={r?.lost ? "" : undefined}
            >
              <span aria-hidden>{!r ? "○" : r.lost ? "?" : r.correct === true ? "✓" : r.correct === false ? "✗" : "●"}</span>
              <span className="sr-only">{!r ? t("live.notAnswered") : r.lost ? t("live.channel.clicker.lostOne") : r.correct === true ? t("option.correct") : r.correct === false ? t("option.incorrect") : t("option.sent")}</span>
              <span className="max-w-32 truncate">{label}</span>
              {snapshot.away_respondents?.includes(rid) && <span aria-label={t("live.awayOne")}>⇠</span>}
              {r && !anonymous && r.channel && r.channel !== "web" && (
                <span
                  className="rounded-full border px-1 text-xs"
                  title={t(`live.channel.names.${r.channel}`, { defaultValue: r.channel })}
                  data-room-channel={r.channel}
                >
                  <span aria-hidden>{t(`live.channel.short.${r.channel}`, { defaultValue: r.channel })}</span>
                  <span className="sr-only">{t(`live.channel.names.${r.channel}`, { defaultValue: r.channel })}</span>
                </span>
              )}
              {r && <span className="max-w-24 truncate font-mono opacity-80">{String(r.answer)}</span>}
            </li>
          );
        })}
      </ul>
      {shortText && (
        <div className="flex flex-col gap-2" aria-label={t("live.clusters.label")}>
          <h3 className="text-sm font-semibold">{t("live.clusters.title", { n: clusters.length })}</h3>
          {ended && <p className="text-sm text-[color:var(--ink-2)]">{t("live.clusters.endedFrozen")}</p>}
          {clusters.length === 0 && <p className="text-sm text-[color:var(--ink-2)]">{t("live.clusters.none")}</p>}
          <ul className="flex flex-col gap-2">
            {clusters.map((c) => {
              const tone = c.verdict === true ? "var(--secure)" : c.verdict === false ? "var(--not-yet)" : "var(--ink-2)";
              return (
                <li key={c.cluster_id} className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] border p-2 text-sm" style={{ borderColor: tone }}>
                  <span className="num min-w-8 rounded-full bg-[color:var(--bg)] px-2 text-center" aria-label={t("live.clusters.count", { n: c.count })}>{c.count}</span>
                  <span className="min-w-0 flex-1 break-words">{c.label}</span>
                  {c.verdict == null && c.suggested != null && (
                    <span className="rounded-full border px-2 text-sm" style={{ borderColor: "var(--draft)", color: "var(--draft)" }}>
                      {c.suggested ? t("live.clusters.suggestCorrect") : t("live.clusters.suggestIncorrect")}
                    </span>
                  )}
                  <span className="sr-only">{c.verdict === true ? t("option.correct") : c.verdict === false ? t("option.incorrect") : t("live.clusters.unlabelled")}</span>
                  <span className="flex gap-1" role="group" aria-label={t("live.clusters.label")}>
                    <Button variant={c.verdict === true ? "primary" : "secondary"} disabled={ended} title={ended ? t("live.refuse.ended") : undefined} onClick={() => onLabel(c.cluster_id, true)}>{t("live.clusters.correct")}</Button>
                    <Button variant={c.verdict === false ? "primary" : "secondary"} disabled={ended} title={ended ? t("live.refuse.ended") : undefined} onClick={() => onLabel(c.cluster_id, false)}>{t("live.clusters.incorrect")}</Button>
                    {c.verdict != null && <Button variant="tertiary" disabled={ended} title={ended ? t("live.refuse.ended") : undefined} onClick={() => onLabel(c.cluster_id, null)}>{t("live.clusters.clear")}</Button>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
